import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useNotificationsStore } from '../stores/notificationsStore';
import { useFriendsStore } from '../stores/friendsStore';
import { usePostsStore } from '../stores/postsStore';

const Notifications = () => {
  const navigate = useNavigate();
  const { accessToken, profile } = useAuthStore();
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    fetchNotifications,
    markAsViewed
  } = useNotificationsStore();
  const { acceptFriendRequest, rejectFriendRequest } = useFriendsStore();
  const { jumpToPost } = usePostsStore();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);

  // Fetch notifications when opened (always refresh to get latest)
  useEffect(() => {
    if (isOpen && accessToken) {
      fetchNotifications(accessToken, true);
    }
  }, [isOpen, accessToken, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Infinite scroll for loading more notifications
  useEffect(() => {
    if (!isOpen || !scrollRef.current) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !isLoading) {
        fetchNotifications(accessToken, false);
      }
    };

    const scrollEl = scrollRef.current;
    scrollEl.addEventListener('scroll', handleScroll);
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [isOpen, hasMore, isLoading, accessToken, fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    console.log('Notification clicked:', notification);

    // Mark as viewed
    if (!notification.viewed_at) {
      await markAsViewed(accessToken, notification.id);
    }

    // Close dropdown
    setIsOpen(false);

    // Navigate based on notification type
    if (notification.post_id || notification.reply_id) {
      // Post or reply notification - navigate with URL params so Dashboard can handle it
      if (notification.post_id) {
        // New notifications have post_id - use jumpToPost param
        console.log('New notification - navigating with jumpToPost param');
        const params = new URLSearchParams({
          jumpToPost: notification.post_id
        });
        if (notification.reply_id) {
          params.append('reply', notification.reply_id);
        }
        navigate(`/profile/${profile?.id}/page/dashboard?${params.toString()}`);
      } else {
        // Old notifications only have reply_id - fallback to old behavior
        console.log('Old notification, using fallback navigation');
        navigate(`/profile/${profile?.id}/page/dashboard?post=${notification.reply_id}`);
      }
    } else if (notification.friendship_id) {
      // Friend request notification - scroll to friends list
      navigate(`/profile/${profile?.id}/page/dashboard#friends`);
    }
  };

  const handleAcceptFriend = async (e, notification) => {
    e.stopPropagation();
    if (!accessToken || !notification.friendship_id || !profile) return;

    const result = await acceptFriendRequest(notification.friendship_id, accessToken, profile.id);
    if (result.success) {
      // Mark notification as viewed
      await markAsViewed(accessToken, notification.id);
      // Refresh notifications to update the text
      fetchNotifications(accessToken, true);
    }
  };

  const handleRejectFriend = async (e, notification) => {
    e.stopPropagation();
    if (!accessToken || !notification.friendship_id || !profile) return;

    const result = await rejectFriendRequest(notification.friendship_id, accessToken, profile.id);
    if (result.success) {
      // Mark notification as viewed
      await markAsViewed(accessToken, notification.id);
      // Refresh notifications to update the text
      fetchNotifications(accessToken, true);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const hasFriendActions = (notification) => {
    return notification.friendship_id &&
           notification.friendship_status_code === 'PENDING';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notifications Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        title="Notifications"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {isOpen && (
        <div className="fixed md:absolute right-4 md:right-0 left-4 md:left-auto mt-2 w-auto md:w-96 surface border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-primary">Notifications</h3>
          </div>

          {/* Notifications List */}
          <div
            ref={scrollRef}
            className="max-h-96 overflow-y-auto"
          >
            {notifications.length === 0 && !isLoading ? (
              <div className="px-4 py-8 text-center text-secondary">
                <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors ${
                    notification.viewed_at
                      ? 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      : 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Unread Indicator */}
                    {!notification.viewed_at && (
                      <div className="w-2 h-2 mt-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Notification Text */}
                      <p className="text-sm text-primary break-words">
                        {notification.notification_text}
                      </p>

                      {/* Time */}
                      <p className="text-xs text-secondary mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>

                      {/* Friend Request Actions */}
                      {hasFriendActions(notification) && (
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={(e) => handleAcceptFriend(e, notification)}
                            className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            onClick={(e) => handleRejectFriend(e, notification)}
                            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="px-4 py-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            )}

            {/* Load More Button */}
            {!isLoading && hasMore && notifications.length > 0 && (
              <button
                onClick={() => fetchNotifications(accessToken, false)}
                className="w-full px-4 py-3 text-sm text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Load more
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
